import { MessageAction } from '@nextforce/api'
import { Icon, makeStyles, useTheme } from '@rneui/themed'
import { t } from 'app/i18l'
import React, { JSX } from 'react'
import { Text, View } from 'react-native'
import {
  Menu,
  MenuOption,
  MenuOptions,
  MenuTrigger,
  renderers,
} from 'react-native-popup-menu'
import { useSafeAreaInsets } from 'react-native-safe-area-context'

interface RefObject<T> {
  current: T | null
}

const getActionIcon = (actionName: MessageAction['name']) => {
  switch (actionName) {
    case 'copyMessage':
      return { name: 'copy', type: 'feather' }
    case 'pinMessage':
    case 'unpinMessage':
      return { name: 'pin', type: 'entypo' }
    case 'reply':
      return { name: 'reply', type: 'material' }
    case 'sendDirectMessage':
      return {
        name: 'send',
        type: 'feather',
      }
    case 'mark':
    case 'unmark':
      return { name: 'bookmark-outline', type: 'material-design' }
    case 'deleteOwn':
    case 'deleteOthers':
      return { name: 'delete', type: 'material' }
    case 'showReadersOwn':
    case 'showReadersOthers':
      return { name: 'eye-check-outline', type: 'material-design' }
    default:
      return null
  }
}

interface Props {
  menuRef: RefObject<Menu | null | undefined>
  actions: MessageAction[] | undefined
  onAction: (action: MessageAction) => void
  onClose?: () => void
  onPlusPress?: (value: boolean) => void
}

export default function ActionSheetMenu({
  menuRef,
  actions,
  onAction,
  onClose,
  onPlusPress,
}: Props): JSX.Element {
  const styles = useStyles()
  const { theme } = useTheme()
  const { bottom } = useSafeAreaInsets()
  const availableActions = actions || []
  const emojiActions = availableActions.filter(
    action => action.name === 'reaction'
  )
  const otherActions = availableActions.filter(
    action => action.name !== 'reaction'
  )
  const ReactionsRow = () => {
    return (
      <View style={styles.reactionsContainer}>
        {emojiActions.map(action => (
          <MenuOption
            key={action.emoji}
            onSelect={() => onAction(action)}
            style={styles.reactionOption}
          >
            <Text style={styles.reactionEmoji}>{action.emoji}</Text>
          </MenuOption>
        ))}
        {emojiActions.length ? (
          <MenuOption
            onSelect={() => onPlusPress && onPlusPress(true)}
            style={styles.reactionOption}
          >
            <Icon
              name="plus"
              type="entypo"
              color={theme.colors.grey3}
              containerStyle={styles.emojiPlusContainer}
            />
          </MenuOption>
        ) : null}
      </View>
    )
  }

  return (
    <View>
      <Menu
        renderer={renderers.SlideInMenu}
        ref={ref => {
          menuRef.current = ref
        }}
        onClose={onClose}
      >
        <MenuTrigger />
        <MenuOptions
          optionsContainerStyle={[
            styles.optionsContainer,
            { paddingBottom: bottom },
          ]}
        >
          {emojiActions.length > 0 && <ReactionsRow />}

          {otherActions.map(action => {
            const isDeleteAction =
              action.name === 'deleteOwn' || action.name === 'deleteOthers'
            const iconProps = getActionIcon(action.name)

            return (
              <MenuOption
                key={action.name}
                onSelect={() => onAction(action)}
                style={styles.actionOption}
              >
                {iconProps && (
                  <Icon
                    {...iconProps}
                    size={22}
                    color={
                      isDeleteAction ? theme.colors.red : theme.colors.grey2
                    }
                    containerStyle={styles.actionIcon}
                  />
                )}
                <Text
                  style={[
                    styles.actionText,
                    isDeleteAction && styles.deleteText,
                  ]}
                >
                  {action.title || t(action.name)}
                </Text>
              </MenuOption>
            )
          })}
        </MenuOptions>
      </Menu>
    </View>
  )
}

const useStyles = makeStyles(theme => ({
  optionsContainer: {
    backgroundColor:
      theme.mode === 'dark' ? theme.colors.grey11 : theme.colors.white,
    borderTopLeftRadius: 16,
    borderTopRightRadius: 16,
    paddingHorizontal: 8,
  },
  reactionsContainer: {
    flexDirection: 'row',
    justifyContent: 'space-around',
    alignItems: 'center',
    paddingVertical: 12,
    borderBottomWidth: 1,
    borderBottomColor:
      theme.mode === 'dark' ? theme.colors.grey4 : theme.colors.grey7,
  },
  reactionOption: {
    padding: 6,
  },
  reactionEmoji: {
    fontSize: 32,
  },
  emojiPlusContainer: {
    backgroundColor:
      theme.mode === 'dark' ? theme.colors.grey13 : theme.colors.grey7,
    borderRadius: 99,
    padding: 8,
  },
  actionOption: {
    flexDirection: 'row',
    justifyContent: 'flex-start',
    alignItems: 'center',
    paddingVertical: 16,
    paddingHorizontal: 16,
  },
  actionIcon: {
    marginRight: 16,
  },
  actionText: {
    fontSize: 16,
    color: theme.colors.black,
  },
  deleteText: {
    color: theme.colors.red,
  },
}))
